import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import tokenManager from '../utils/tokenManager';
import axiosInstance from '../components/api/axiosInstance';
import logger from '../utils/logger';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const isMounted = useRef(true);

  const fetchUserData = useCallback(async (force = false) => {
    try {
      // CRITICAL FIX: Synchronize authentication across domains first
      // This handles pending tokens from cross-domain redirects (e.g., from accounts.aiinak.com)
      const isAuthenticated = await tokenManager.synchronizeAuthenticationAcrossDomains();

      if (!isAuthenticated) {
        if (isMounted.current) { setLoading(false); setIsInitialized(true); }
        return;
      }

      // Get token after synchronization
      const token = tokenManager.getToken();
      if (!token) {
        if (isMounted.current) { setLoading(false); setIsInitialized(true); }
        return;
      }

      // Always fetch fresh data from auth service
      const response = await axiosInstance.get('/api/user');

      if (response.data && isMounted.current) {
        setUserData(response.data);
      }
    } catch (error) {
      logger.error('Error fetching user data:', error);
      if (error.response?.status === 401 && isMounted.current) {
        tokenManager.removeToken();
        setUserData(null);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
        setIsInitialized(true);
      }
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // Prevent multiple initializations
    if (isInitialized) {
      return;
    }

    // Simply fetch user data on mount - match the working aimail pattern
    fetchUserData();

    return () => { isMounted.current = false; };
  }, [isInitialized, fetchUserData]); // Run once when component mounts

  // Calculate admin access like main app
  const calculateAdminAccess = useCallback((user) => {
    if (!user) {
      return false;
    }

    // First, determine if user has admin role permissions (original logic)
    let hasRoleBasedAccess = false;

    // Check the hasAdminAccess field from auth service (primary check)
    if (user.hasAdminAccess !== undefined) {
      hasRoleBasedAccess = user.hasAdminAccess;
    }

    // Direct admin flags (continue original logic)
    if (user.isAdmin || user.isBusinessOwner) {
      hasRoleBasedAccess = true;
    }
    
    // Check role-based permissions (continue original logic)
    if (!hasRoleBasedAccess && user.roleInfo) {
      const roleInfo = user.roleInfo;
      const permissions = roleInfo.permissions || [];
      
      // Check if role has admin-level permissions (level 60+ = Manager and above)
      const hasAdminRole = roleInfo.level >= 60 ||
                          permissions.includes('users:create') ||
                          permissions.includes('users:update') ||
                          permissions.includes('users:delete') ||
                          permissions.includes('security:manage_roles') ||
                          permissions.includes('admin:full_access');
      
      if (hasAdminRole) {
        hasRoleBasedAccess = true;
      }
    }
    
    // Check business roleInfo (legacy support)
    if (!hasRoleBasedAccess && user.business?.roleInfo) {
      const roleInfo = user.business.roleInfo;
      const permissions = roleInfo.permissions || [];
      
      // Check if role has admin-level permissions
      const hasAdminRole = roleInfo.level >= 60 ||
                          permissions.includes('users:create') ||
                          permissions.includes('users:update') ||
                          permissions.includes('users:delete') ||
                          permissions.includes('settings:update') ||
                          permissions.includes('security:manage_roles') ||
                          permissions.includes('admin:manage_business');
      
      if (hasAdminRole) {
        hasRoleBasedAccess = true;
      }
    }
    
    // Check role string for admin designation
    if (!hasRoleBasedAccess && (user.role === 'admin' || user.role === 'super_admin' || user.role === 'manager')) {
      hasRoleBasedAccess = true;
    }
    
    // Check userType for admin designation
    if (!hasRoleBasedAccess && (user.userType === 'admin' || user.accountType === 'admin')) {
      hasRoleBasedAccess = true;
    }
    
    // Legacy business permission checks (like main app)
    if (!hasRoleBasedAccess && user.accountType && ['business', 'external'].includes(user.accountType) && user.business?.role === 'admin') {
      hasRoleBasedAccess = true;
    }
    
    if (!hasRoleBasedAccess && (user.business?.permissions || []).includes('manage_settings')) {
      hasRoleBasedAccess = true;
    }
    
    // 🔒 CRITICAL: Business users with admin roles MUST have active plans
    // Admin roles don't bypass plan validation for business users
    
    const hasExplicitAdminRole = user.isAdmin || user.isBusinessOwner;
    const isBusinessAccount = user.accountType === 'business' || user.userType === 'business';

    if (hasExplicitAdminRole && isBusinessAccount) {
      // NEW: Check if this is a delegated admin with sufficient role level
      const isDelegatedAdmin = user.roleInfo?.level >= 70 && !user.isBusinessOwner;
      const hasHighLevelRole = user.roleInfo?.name === 'super_admin' ||
                              user.roleInfo?.name === 'admin';

      if (isDelegatedAdmin && hasHighLevelRole) {
        return true; // BYPASS PLAN VALIDATION
      }

      // Business admins must have active subscription plans (including trial)
      if (user.business?.subscriptionDetails) {
        const subscription = user.business.subscriptionDetails;

        // Allow both 'active' and 'trial' statuses
        if (subscription.plan === null || (subscription.status !== 'active' && subscription.status !== 'trial')) {
          return false;
        }

        return true;
      } else {
        return false;
      }
    } else if (hasExplicitAdminRole && !isBusinessAccount) {
      // Non-business admins (system admins) bypass plan validation
      return true;
    }
    
    // For non-explicit admins (role-based only), check plan validation
    if (hasRoleBasedAccess && (user.accountType === 'business' || user.userType === 'business')) {
      if (user.business?.subscriptionDetails) {
        const subscription = user.business.subscriptionDetails;

        // Block access if no active plan (for role-based business admins only)
        // Allow both 'active' and 'trial' statuses
        if (subscription.plan === null || (subscription.status !== 'active' && subscription.status !== 'trial')) {
          return false;
        }
      } else {
        // Business user without subscription details = no access
        return false;
      }
    }

    return hasRoleBasedAccess;
  }, []);

  // Computed properties - memoized to prevent unnecessary re-renders
  const computedValues = useMemo(() => {
    const isAuthenticated = !!userData && !!tokenManager.getToken();
    const userEmail = userData?.email || '';
    const firstName = userData?.firstName || '';
    const lastName = userData?.lastName || '';
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : firstName || lastName || '';
    const profilePicture = userData?.profilePicture || null;
    const hasAdminAccess = calculateAdminAccess(userData);
    const isAdmin = userData?.isAdmin || hasAdminAccess || false;
    const isBusinessOwner = userData?.isBusinessOwner || false;
    const accountType = userData?.accountType || userData?.userType || '';
    
    return {
      isAuthenticated,
      userEmail,
      firstName,
      lastName,
      fullName,
      profilePicture,
      hasAdminAccess,
      isAdmin,
      isBusinessOwner,
      accountType
    };
  }, [userData, calculateAdminAccess]);

  const logout = async () => {
    await tokenManager.logout({
      redirect: true,
      redirectUrl: '/login'
    });
    setUserData(null);
  };

  const value = useMemo(() => ({
    // User data
    userData,
    userEmail: computedValues.userEmail,
    firstName: computedValues.firstName,
    lastName: computedValues.lastName,
    fullName: computedValues.fullName,
    profilePicture: computedValues.profilePicture,
    
    // Authentication state
    isAuthenticated: computedValues.isAuthenticated,
    loading,
    isInitialized,
    
    // Permissions
    isAdmin: computedValues.isAdmin,
    isBusinessOwner: computedValues.isBusinessOwner,
    hasAdminAccess: computedValues.hasAdminAccess,
    accountType: computedValues.accountType,
    
    // Actions
    fetchUserData,
    logout,
    
    // Update user data
    setUserData,
    
    // Force refresh authentication state
    refreshAuth: () => {
      // Force a fresh fetch of user data
      setIsInitialized(false);
      return fetchUserData(true);
    }
  }), [computedValues, userData, loading, isInitialized, fetchUserData, logout, setUserData]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};